import type { Category } from 'containers/categories/types'
import type { Item } from 'containers/items/types'
import { RecipeCategory } from 'containers/recipeCategories/types'
import { Recipe } from 'containers/recipes/types'

export const getCategoryOptions = (categories: Category[] = []) => {
    const options = categories.map(({ id, name }) => ({
        label: name,
        value: id.toString()
    }))

    return [{ label: 'Uncategorized', value: 'none' }, ...options]
}

export const getRecipeCategoryOptions = (recipeCategories: RecipeCategory[] = []) => {
    const options = recipeCategories.map(({ id, name }) => ({
        label: name,
        value: id.toString()
    }))

    return [{ label: 'Uncategorized', value: 'none' }, ...options]
}

export const getCategoryColor = (categories: Category[], catName: string) => {
    const index = categories.findIndex(({ name }) => catName === name)
    const num = index % 6

    switch (num) {
        case 0:
            return 'bg-red-500'
        case 1:
            return 'bg-orange-500'
        case 2:
            return 'bg-lime-500'
        case 3:
            return 'bg-sky-500'
        case 4:
            return 'bg-fuchsia-500'
        case 5:
            return 'bg-indigo-500'
        default:
            return 'bg-gray-400'
    }
}

export const getRecipeCategoryColor = (recipeCategories: RecipeCategory[], catName: string) => {
    const index = recipeCategories.findIndex(({ name }) => catName === name)
    const num = index % 6

    switch (num) {
        case 0:
            return 'bg-red-500'
        case 1:
            return 'bg-orange-500'
        case 2:
            return 'bg-lime-500'
        case 3:
            return 'bg-sky-500'
        case 4:
            return 'bg-fuchsia-500'
        case 5:
            return 'bg-indigo-500'
        default:
            return 'bg-gray-400'
    }
}

export const getExistingCategories = (itemsList: Item[]) => {
    const mapped = itemsList.map(({ category }) => category || { name: 'Uncategorized', id: -1 })
    const filtered = mapped.filter((category, index, arr) => arr.findIndex((categoryNested) => category.id === categoryNested.id) === index)
    const sorted = filtered.sort((a, b) => {
        if (b.id === -1) return -1
        if (a.id === -1) return 1
        return a.name.localeCompare(b.name)
    })

    return sorted
}

export const getExistingRecipeCategories = (recipesList: Recipe[]) => {
    const mapped = recipesList.map(({ recipe_category }) => recipe_category || { name: 'Uncategorized', id: -1 })
    const filtered = mapped.filter(
        (recipeCategory, index, arr) => arr.findIndex((recipeCategoryNested) => recipeCategory.id === recipeCategoryNested.id) === index
    )
    const sorted = filtered.sort((a, b) => {
        if (b.id === -1) return -1
        if (a.id === -1) return 1
        return a.name.localeCompare(b.name)
    })

    return sorted
}

export function handleEnter(cb?: () => void) {
    function handler(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' || e.code === 'Enter') {
            e.preventDefault()
            cb?.()
        }
    }

    return handler
}

export function pluralize(word: string, num: number) {
    return num === 1 ? word : word + 's'
}

export function truncate(text: string, maxLength: number = 40) {
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

type Obj = { name: string; sort_order: number | null }
export function sortBySortOrderThenAlpha(a: Obj, b: Obj) {
    if ((a.sort_order == null && b.sort_order == null) || a.sort_order === b.sort_order) {
        return a.name > b.name ? 1 : -1
    }
    if (a.sort_order == null) {
        return 1
    }
    if (b.sort_order == null) {
        return -1
    }
    return a.sort_order - b.sort_order
}

export function forceConsistentlySteppedSortOrder<T extends string | number>(
    currentSortOrders: {
        recipe_item_id: T
        sort_order: number | null
        ingredient_group?: string | null
    }[]
): {
    recipe_item_id: T
    sort_order: number
}[] {
    const sorted = currentSortOrders.sort((a, b) => {
        if (a.sort_order === null) {
            return 1
        }
        if (b.sort_order === null) {
            return -1
        }

        return a.sort_order - b.sort_order
    })

    return sorted.map((item, index) => ({
        ...item,
        sort_order: index
    }))
}

export function reorderOnDrag<T extends string | number>(
    currentSortOrders: {
        recipe_item_id: T
        sort_order: number | null
        ingredient_group?: string | null
    }[],
    itemToMoveId: T,
    itemNewSortOrder: number
): {
    recipe_item_id: T
    sort_order: number | null
    ingredient_group?: string | null
}[] {
    const itemToMove = currentSortOrders.find((item) => item.recipe_item_id === itemToMoveId)

    if (!itemToMove) {
        return currentSortOrders
    }

    const oldSortOrder = itemToMove.sort_order

    const reordered = currentSortOrders.map((item) => {
        if (item.recipe_item_id === itemToMoveId) {
            return {
                ...item,
                sort_order: itemNewSortOrder
            }
        }

        // If the item being moved has no sort order, it is currently at the end.
        // Moving it to a numbered position shifts that position and everything after it.
        if (oldSortOrder === null && item.sort_order !== null && item.sort_order >= itemNewSortOrder) {
            return {
                ...item,
                sort_order: item.sort_order + 1
            }
        }

        // Moving down.
        if (
            oldSortOrder !== null &&
            oldSortOrder < itemNewSortOrder &&
            item.sort_order !== null &&
            item.sort_order > oldSortOrder &&
            item.sort_order <= itemNewSortOrder
        ) {
            return {
                ...item,
                sort_order: item.sort_order - 1
            }
        }

        // Moving up.
        if (
            oldSortOrder !== null &&
            oldSortOrder > itemNewSortOrder &&
            item.sort_order !== null &&
            item.sort_order >= itemNewSortOrder &&
            item.sort_order < oldSortOrder
        ) {
            return {
                ...item,
                sort_order: item.sort_order + 1
            }
        }

        return item
    })

    // nulls go last. Within the numbered items, preserve their existing order
    // where sort_order values are equal.
    const sorted = reordered.sort((a, b) => {
        if (a.sort_order === null) {
            return 1
        }
        if (b.sort_order === null) {
            return -1
        }

        return a.sort_order - b.sort_order
    })

    // Compress numbered sort orders to 0, 1, 2, ...
    let nextSortOrder = 0

    return sorted.map((item) => {
        if (item.sort_order === null) {
            return item
        }

        return {
            ...item,
            sort_order: nextSortOrder++
        }
    })
}
